import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, User, Compass, ArrowRight } from 'lucide-react';
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle } from '@retreatflow360/ui';
import { useAuth } from '@/stores/auth';

export default function DashboardPage() {
  const { user } = useAuth();

  const quickActions = [
    {
      title: 'Browse Events',
      description: 'Discover upcoming retreats and experiences',
      icon: Compass,
      link: '/events',
      color: 'bg-blue-500',
    },
    {
      title: 'My Bookings',
      description: 'View and manage your reservations',
      icon: Calendar,
      link: '/my-bookings',
      color: 'bg-green-500',
    },
    {
      title: 'My Profile',
      description: 'Update your personal information',
      icon: User,
      link: '/profile',
      color: 'bg-purple-500',
    },
  ];

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Welcome Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold">
          Welcome back, {user?.firstName || 'Traveler'}!
        </h1>
        <p className="mt-2 text-muted-foreground">
          Ready for your next retreat experience?
        </p>
      </motion.div>

      {/* Quick Actions */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="mb-8"
      >
        <h2 className="mb-4 text-xl font-semibold">Quick Actions</h2>
        <div className="grid gap-4 md:grid-cols-3">
          {quickActions.map((action, index) => (
            <motion.div
              key={action.title}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 + index * 0.05 }}
            >
              <Link to={action.link}>
                <Card className="h-full transition-all hover:shadow-lg hover:border-primary/50">
                  <CardHeader className="pb-2">
                    <div className={`mb-2 inline-flex h-10 w-10 items-center justify-center rounded-lg ${action.color} text-white`}>
                      <action.icon className="h-5 w-5" />
                    </div>
                    <CardTitle className="text-lg">{action.title}</CardTitle>
                    <CardDescription>{action.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center text-sm text-primary">
                      Go to {action.title.toLowerCase()}
                      <ArrowRight className="ml-1 h-4 w-4" />
                    </div>
                  </CardContent>
                </Card>
              </Link>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Upcoming Bookings Placeholder */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25 }}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Upcoming Retreats</h2>
          <Button variant="outline" size="sm" asChild>
            <Link to="/my-bookings">View all</Link>
          </Button>
        </div>
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Calendar className="mb-4 h-12 w-12 text-muted-foreground/50" />
            <h3 className="mb-2 text-lg font-medium">No upcoming retreats</h3>
            <p className="mb-4 text-sm text-muted-foreground">
              You don't have any upcoming bookings. Start exploring our retreats!
            </p>
            <Button asChild>
              <Link to="/events">
                <Compass className="mr-2 h-4 w-4" />
                Browse Events
              </Link>
            </Button>
          </CardContent>
        </Card>
      </motion.div>

      {/* Featured Events Teaser */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35 }}
        className="mt-8"
      >
        <Card className="bg-gradient-to-r from-primary/10 to-primary/5 border-primary/20">
          <CardContent className="flex flex-col items-center justify-between gap-4 py-6 md:flex-row">
            <div>
              <h3 className="text-lg font-semibold">Discover Your Next Adventure</h3>
              <p className="text-sm text-muted-foreground">
                Explore our curated collection of wellness retreats, yoga escapes, and transformative experiences.
              </p>
            </div>
            <Button asChild>
              <Link to="/events">
                Explore Events
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}
